import { inject, Injectable } from '@angular/core';

import { timer, Observable, ReplaySubject, Subscription, Subject } from 'rxjs';
import { connect } from 'socket.io-client';

import { LoggerService } from 'app/services/logger.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { AlertService } from '../services/alert.service';
import { LocalStorageService } from '../services/local-storage.service';

export interface RealtimeMessage<T> {
  event: string;
  verb: 'CREATE' | 'UPDATE' | 'DELETE';
  sequenceId: string;
  object: T;
  extra: Map<string, string>;
}

export interface RealtimeEventHandler<T> {
  (data: RealtimeMessage<T>);
}

export interface RealtimeEventHandlerMap<T> {
  [key: string]: RealtimeEventHandler<T>;
}

export class RealtimeConnection {
  socket: SocketIOClient.Socket;

  constructor(socket: SocketIOClient.Socket) {
    this.socket = socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

export interface RealtimeSocketConnection {
  socket: SocketIOClient.Socket;
  socketConsumer: string;
  currentAttempt: number;
  maxRetryAttempts: number;
}

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  token = '';
  private socketConnectionMap = new Map<string, RealtimeSocketConnection>();
  private connection = new ReplaySubject<boolean>();

  private retryDelays = [5000, 10000, 30000, 60000]; // Delay intervals in ms
  private retrySubscriptions = new Map<string, Subscription>();
  private manualRetry$ = new Subject<{ socketConsumer: string; socket: SocketIOClient.Socket }>();
  readonly maxRetryAttempts = 5;

  private renewTokenSubject = new Subject<void>();
  renewToken$ = this.renewTokenSubject.asObservable();
  private localStorageService = inject(LocalStorageService);
  private readonly logger = inject(LoggerService);

  constructor(private alertService: AlertService) {
    this.token = this.localStorageService.getItem('rt-token');
    this.manualRetry$.subscribe((socketInfo) => {
      this.resetRetry(socketInfo.socketConsumer);
      this.retryConnection(socketInfo.socket);
    });
  }

  get context(): string {
    let ctx = '/';
    const appConfig = (window as any).appConfig;
    if (appConfig?.rtrelay?.context) {
      ctx = appConfig.rtrelay.context;
    }
    return ctx;
  }

  getConnectionStatus(): Observable<boolean> {
    return this.connection.asObservable();
  }

  static isLogicallyDeleted(data: RealtimeMessage<{ id: string; projectId: string }>): boolean {
    return data.verb === 'UPDATE' && data.extra?.['isLogicallyDeleted'] === 'true';
  }

  static getUrl(namespace: string = '') {
    const appConfig = (window as any).appConfig;
    if (appConfig && appConfig.rtrelay && appConfig.rtrelay.url) {
      return `${appConfig.rtrelay.url}/${namespace}`;
    }
    return `http://localhost:8099/${namespace}`;
  }

  setToken(token: string) {
    this.token = token;
    this.localStorageService.setItem('rt-token', token, 'local', true);
  }

  on<T>(
    namespace: string,
    events: RealtimeEventHandlerMap<T>,
    socketConsumer: string
  ): RealtimeConnection {
    if (!this.token) {
      this.logger.debug('No realtime relay token set');
      return;
    }
    const url = RealtimeService.getUrl(namespace);
    this.logger.debug(`${socketConsumer} connecting to ${url} with context ${this.context}`);
    const opts: SocketIOClient.ConnectOpts = { path: this.context };
    if (url.startsWith('https')) {
      opts.secure = true;
    }
    const socket = connect(url, opts);

    socket.on('connect', () => {
      this.logger.debug(`${socketConsumer} connected, sending token...`);
      socket.emit('settoken', { token: this.token });
      this.handleConnection(socket, socketConsumer);
    });
    socket.on('disconnect', (err: string) => {
      this.logger.debug(`${socketConsumer} disconnected by: ${err}`);
      this.handleDisconnect(socket, err, socketConsumer ?? namespace);
    });
    socket.on('message', (message: any) => {
      this.logger.debug('message', { message });
    });
    socket.on('auth-failed', (err: any) => {
      this.logger.debug('auth failed', err);
      if (err === 'Token not valid') {
        this.renewTokenSubject.next();
      }
    });
    socket.on('error', (err) => {
      this.logger.debug('error occured', { error: err });
    });

    socket.on('connect_error', (error) => {
      this.logger.debug('connect error occured on socket', { error });
      this.scheduleReconnect(socket, 'websocket.connection-error', socketConsumer);
    });
    socket.on('connect_timeout', (timeout) => {
      this.logger.debug('connect timout occured on socket', { socket, timeout });
      this.scheduleReconnect(socket, 'websocket.connection-timeout', socketConsumer);
    });
    socket.on('reconnect_failed', () => {
      this.logger.debug('reconnect failed', socket);
      this.scheduleReconnect(socket, 'websocket.reconnect-failed', socketConsumer);
    });

    socket.on('reconnecting', (attemptNumber) => {
      this.logger.debug('socket reconnecting', { socket, attemptNumber });
    });
    socket.on('reconnect_error', (error) => {
      this.logger.debug('reconnect error occured on socket', { socket, error });
      // We need to disconnect manually if we don't want to flood the console with polling errors (we will try to reconnect automatically)
      socket.disconnect();
    });
    Object.keys(events).forEach((event: string) => {
      const fn = events[event];
      // TODO: LRU to bound the size?
      const seen = new Set<string>();
      socket.on(event, (data: RealtimeMessage<T>) => {
        if (seen.has(data.sequenceId)) {
          this.logger.debug(`dropping duplicate ${data.sequenceId}`);
          return;
        }
        seen.add(data.sequenceId);
        fn(data);
      });
    });
    return new RealtimeConnection(socket);
  }

  private handleConnection(socket: SocketIOClient.Socket, socketConsumer: string): void {
    const existingConnection = this.socketConnectionMap.get(socketConsumer);
    const mapValue: RealtimeSocketConnection = {
      socket,
      socketConsumer,
      maxRetryAttempts: this.maxRetryAttempts,
      currentAttempt: existingConnection ? existingConnection.currentAttempt + 1 : 0,
    };
    this.socketConnectionMap.set(socketConsumer, mapValue);
    this.connection.next(!this.hasDisconnectedSocketThatWasntAskedToBeDisconnected());
  }

  private handleDisconnect(socket: SocketIOClient.Socket, err: string, socketConsumer?: string) {
    switch (err) {
      case 'io server disconnect':
        this.logger.debug(
          `socket ${socket.nsp} for consumer ${socketConsumer} was disconnected by server initiating reconnect manually`
        );
        // if we have a server disconnect, we need to reconnect manually
        this.scheduleReconnect(socket, 'websocket.server-disconnected', socketConsumer);
        break;
      case 'io client disconnect':
        this.logger.debug(`socket ${socket.nsp} was disconnected by client`);
        // if we have a client disconnect ther shouldn't be any connection to that socket
        this.socketConnectionMap.delete(socketConsumer);
        break;
      default:
        // other events don't need special handling socket is reconnecting automatically
        this.logger.debug(`socket ${socket.nsp} for consumer ${socketConsumer} has error: ${err}`);
        break;
    }
    this.connection.next(!this.hasDisconnectedSocketThatWasntAskedToBeDisconnected());
  }

  private hasDisconnectedSocketThatWasntAskedToBeDisconnected(): boolean {
    return [...this.socketConnectionMap.values()].some(
      (socketConnection) => socketConnection?.socket?.disconnected
    );
  }

  private getSocketsThatReachedMaxAttempt(): RealtimeSocketConnection[] {
    return [...this.socketConnectionMap.values()].filter(
      (socketConnection) =>
        socketConnection?.socket?.disconnected &&
        socketConnection?.currentAttempt >= this.maxRetryAttempts
    );
  }

  private scheduleReconnect(
    socket: SocketIOClient.Socket,
    message: string,
    socketConsumer: string
  ) {
    if (this.retrySubscriptions.has(socketConsumer)) {
      return;
    } // Avoid duplicate timers if one is already running
    this.handleConnection(socket, socketConsumer);
    const realtimeSocketConnection = this.socketConnectionMap.get(socketConsumer);
    const delay =
      this.retryDelays[realtimeSocketConnection.currentAttempt] ||
      this.retryDelays[this.retryDelays.length - 1];
    const terminatableSockets = this.getSocketsThatReachedMaxAttempt();
    if (terminatableSockets?.length) {
      const isCurrentConsumerReachedLimit = terminatableSockets.some(
        (sc) => sc.socketConsumer === socketConsumer
      );
      terminatableSockets.forEach((sc) => this.socketConnectionMap.delete(sc.socketConsumer));
      if (isCurrentConsumerReachedLimit) {
        return;
      }
    }

    this.retrySubscriptions.set(
      socketConsumer,
      timer(delay).subscribe(() => {
        this.retryConnection(socket);
        this.retrySubscriptions.get(socketConsumer)?.unsubscribe();
        this.retrySubscriptions.delete(socketConsumer);
      })
    );
  }

  private retryConnection(socket: SocketIOClient.Socket) {
    socket.connect();
  }

  private resetRetry(socketConsumer: string) {
    const realtimeSocketConnection = this.socketConnectionMap.get(socketConsumer);
    if (realtimeSocketConnection) {
      realtimeSocketConnection.currentAttempt =
        realtimeSocketConnection.currentAttempt < this.retryDelays.length - 1
          ? realtimeSocketConnection.currentAttempt
          : 0;
    }
    this.retrySubscriptions.get(socketConsumer)?.unsubscribe();
    this.retrySubscriptions.delete(socketConsumer);
  }

  public triggerManualRetry(socketInfo: { socketConsumer: string; socket: SocketIOClient.Socket }) {
    this.manualRetry$.next(socketInfo);
  }
}
